import { Form, Input, message, Modal, Upload, Space, Card } from "antd";
import { InboxOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import Button from "../components/Button";
import MyModal from "../components/MyModal";
import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { createReward } from "../actions/acc_actions";
import { useTranslation } from "react-i18next";

const { Dragger } = Upload;

export default function RewardsFormModal({ visible, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const successModalRef = useRef();

  const businessUrn = useSelector((state) => state.account.businessUrn);

  const handleChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);

    if (newFileList.length > 0) {
      const file = newFileList[0].originFileObj;
      form.setFieldsValue({ file });

      const preview = URL.createObjectURL(file);
      setPreviewUrl(preview);
    } else {
      handleRemove();
    }
  };

  const handleRemove = () => {
    setFileList([]);
    setPreviewUrl(null);
    form.setFieldsValue({ file: null });
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("businessUrn", businessUrn);
      formData.append("name", values.name);

      if (values.file) formData.append("file", values.file);

      await createReward(formData);

      if (onSuccess) onSuccess();
      successModalRef.current.showModal();
      form.resetFields();
      handleRemove();
    } catch (err) {
      console.error(err);
      message.error(
        err?.response?.data?.error?.message || t("somethingWentWrong")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        open={visible}
        onCancel={onClose}
        title={t("createReward")}
        footer={null}
        destroyOnClose
      >
        <Card
          bordered={false}
          style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.05)", borderRadius: 16 }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            requiredMark={false}
          >
            <Form.Item
              name="name"
              label={t("rewardName")}
              tooltip={t("rewardNameTooltip")}
              rules={[{ required: true, message: t("pleaseInputRewardName") }]}
            >
              <Input placeholder={t("rewardNamePlaceholder")} />
            </Form.Item>

            <Form.Item label={t("rewardImageOptional")} name="file">
              {!previewUrl ? (
                <Dragger
                  fileList={fileList}
                  beforeUpload={() => false}
                  onChange={handleChange}
                  maxCount={1}
                  accept="image/*"
                  multiple={false}
                  style={{
                    borderRadius: 12,
                    backgroundColor: "#fafafa",
                    padding: "20px 0",
                  }}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined style={{ color: "#1890ff" }} />
                  </p>
                  <p className="ant-upload-text">{t("clickOrDragToUpload")}</p>
                  <p className="ant-upload-hint" style={{ color: "#888" }}>
                    {t("optionalSingleImageUpload")}
                  </p>
                </Dragger>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    border: "1px dashed #d9d9d9",
                    borderRadius: 12,
                    padding: 16,
                    background: "#fafafa",
                  }}
                >
                  <img
                    src={previewUrl}
                    alt={t("preview")}
                    style={{
                      maxWidth: "100%",
                      borderRadius: 8,
                      marginBottom: 16,
                    }}
                  />
                  <Space>
                    <Button
                      type="default"
                      icon={<EditOutlined />}
                      onClick={() => handleRemove()}
                    >
                      {t("replaceImage")}
                    </Button>
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={handleRemove}
                    >
                      {t("remove")}
                    </Button>
                  </Space>
                </div>
              )}
            </Form.Item>

            <Form.Item style={{ textAlign: "center", marginTop: 24 }}>
              <Button htmlType="submit" type="primary" loading={loading}>
                {t("createReward")}
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </Modal>

      <MyModal
        ref={successModalRef}
        title={t("rewardAdded")}
        message={t("rewardCreatedSuccessfully")}
        confirmText={t("backToRewards")}
      />
    </>
  );
}
